import {
  ActionIcon,
  Box,
  Button,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconGripVertical,
  IconMessageCircle,
  IconX,
} from "@tabler/icons-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { ChatLogEntry } from "../hooks/useRoomConnection";

const PANEL_WIDTH = 340;
const PANEL_HEIGHT = 380;

const ROLE_COLORS: Record<string, string | undefined> = {
  player: undefined,
  npc: "cyan.7",
};

function clampPosition(x: number, y: number): { x: number; y: number } {
  const margin = 12;
  const maxX = Math.max(margin, window.innerWidth - PANEL_WIDTH - margin);
  const maxY = Math.max(margin, window.innerHeight - PANEL_HEIGHT - margin);
  return {
    x: Math.min(Math.max(margin, x), maxX),
    y: Math.min(Math.max(margin, y), maxY),
  };
}

function initialPosition(): { x: number; y: number } {
  if (typeof window === "undefined") return { x: 16, y: 200 };
  return clampPosition(16, window.innerHeight - PANEL_HEIGHT - 80);
}

interface Props {
  chatLog: ChatLogEntry[];
  onAskBot: (botId: string, prompt: string) => void;
  onClose?: () => void;
}

export function ChatPanel({ chatLog, onAskBot, onClose }: Props) {
  const viewport = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(initialPosition);
  const dragState = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  useEffect(() => {
    viewport.current?.scrollTo({ top: viewport.current.scrollHeight });
  }, [chatLog.length]);

  const stopDragging = useCallback(() => {
    dragState.current = null;
    document.body.style.userSelect = "";
  }, []);

  const onDragMove = useCallback((event: PointerEvent) => {
    const state = dragState.current;
    if (!state) return;
    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    setPosition(clampPosition(state.originX + dx, state.originY + dy));
  }, []);

  const onDragEnd = useCallback(
    function handleDragEnd() {
      stopDragging();
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", handleDragEnd);
      window.removeEventListener("pointercancel", handleDragEnd);
    },
    [onDragMove, stopDragging]
  );

  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => clampPosition(prev.x, prev.y));
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", onDragEnd);
      window.removeEventListener("pointercancel", onDragEnd);
      stopDragging();
    };
  }, [onDragEnd, onDragMove, stopDragging]);

  const handleHeaderPointerDown = (
    event: ReactPointerEvent<HTMLDivElement>
  ) => {
    if (event.button !== 0) return;
    event.preventDefault();
    dragState.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
    };
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", onDragEnd);
    window.addEventListener("pointercancel", onDragEnd);
  };

  return (
    <Paper
      p={10}
      radius="md"
      style={{
        position: "absolute",
        top: position.y,
        left: position.x,
        width: PANEL_WIDTH,
        height: PANEL_HEIGHT,
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
        background: "#ffffff",
        border: "1px solid rgba(0,0,0,0.10)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        pointerEvents: "auto",
      }}
    >
      <Box
        onPointerDown={handleHeaderPointerDown}
        style={{
          touchAction: "none",
          cursor: "grab",
          marginBottom: 6,
          padding: "6px 10px",
          userSelect: "none",
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap={6} wrap="nowrap">
            <IconGripVertical size={14} color="rgba(0,0,0,0.3)" />
            <IconMessageCircle size={15} color="#ff922b" />
            <Title order={5} fz={13} c="gray.8">
              Chat
            </Title>
          </Group>
          {onClose && (
            <ActionIcon
              variant="subtle"
              size="sm"
              color="gray.4"
              onClick={onClose}
            >
              <IconX size={15} />
            </ActionIcon>
          )}
        </Group>
      </Box>

      <ScrollArea
        viewportRef={viewport}
        style={{ flex: 1, minHeight: 0 }}
        offsetScrollbars
      >
        <Stack gap={4} px={10}>
          {chatLog.map((entry) => (
            <Text key={entry.id} size="sm" c={ROLE_COLORS[entry.role]}>
              <Text span fw={600} c="gray.7">
                {entry.fromName}:
              </Text>{" "}
              {entry.text}
            </Text>
          ))}
          {chatLog.length === 0 && (
            <Text size="sm" c="dimmed">
              No messages yet.
            </Text>
          )}
        </Stack>
      </ScrollArea>

      <Group gap={4} mt={6} px={10}>
        <Button
          variant="light"
          size="compact-xs"
          color="cyan"
          onClick={() => onAskBot("bot-guide", "Give me a game idea")}
        >
          @guide
        </Button>
      </Group>
    </Paper>
  );
}
